import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Moon, Sun } from "lucide-react";

const Section = ({ title, children }) => (
  <div className="mb-8">
    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{title}</h2>
    <div className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed space-y-3">
      {children}
    </div>
  </div>
);

const CookiePolicy = () => {
  const [darkMode, setDarkMode] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem("theme");
    const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    const enabled = saved ? saved === "dark" : prefersDark;
    setDarkMode(enabled);
    document.documentElement.classList.toggle("dark", enabled);
  }, []);

  const toggleTheme = () => {
    const next = !darkMode;
    setDarkMode(next);
    document.documentElement.classList.toggle("dark", next);
    localStorage.setItem("theme", next ? "dark" : "light");
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="flex items-center justify-between mb-8">
          <Link
            to="/"
            className="text-sm text-purple-700 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 font-medium inline-block"
          >
            ← Back to home
          </Link>
          <button
            onClick={toggleTheme}
            aria-label="Toggle theme"
            className="p-2 rounded-lg text-gray-500 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 transition"
          >
            {darkMode ? <Sun className="w-[18px] h-[18px] text-amber-400" /> : <Moon className="w-[18px] h-[18px]" />}
          </button>
        </div>

        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Cookie Policy
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-10">Last updated: July 2026</p>

        <Section title="What are cookies?">
          <p>
            Cookies are small text files stored on your device when you visit
            our site. They help us keep you signed in, remember your
            preferences, and understand how the platform is used so we can
            improve it.
          </p>
        </Section>

        <Section title="How we use cookies">
          <p>We use cookies for the following purposes:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <strong className="text-gray-800 dark:text-gray-200">Essential cookies</strong> — keep you logged in, secure
              your session, and remember items like your wallet balance view.
              These can't be switched off, as the site won't function
              properly without them.
            </li>
            <li>
              <strong className="text-gray-800 dark:text-gray-200">Preference cookies</strong> — remember choices like
              dark/light mode.
            </li>
            <li>
              <strong className="text-gray-800 dark:text-gray-200">Analytics cookies</strong> — help us understand which
              pages are used most, so we can improve the product. These are
              only set if you accept them.
            </li>
          </ul>
        </Section>

        <Section title="Your choices">
          <p>
            When you first visit LarryDigitals, you'll see a banner asking you
            to accept or reject non-essential cookies. You can change your
            mind at any time by clearing your browser's site data for this
            domain, which will show the banner again on your next visit.
          </p>
        </Section>

        <Section title="Third-party cookies">
          <p>
            Some features (such as payment processing for wallet funding) may
            set their own cookies through our payment partners. We don't
            control these directly — refer to the relevant partner's own
            cookie policy for details.
          </p>
        </Section>

        <Section title="Contact us">
          <p>
            Questions about this policy? Reach out via the support chat on
            the site or WhatsApp us directly from your dashboard.
          </p>
        </Section>
      </div>
    </div>
  );
};

export default CookiePolicy;